import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  LabelValueList,
  PencilAltIcon,
  Typography,
  useModal,
} from '@forgeglobal/design-framework'
import { OpportunityDetailsModal } from 'components/opportunities/OpportunityDetailsModal'
import { OwnerChip } from 'components/shared/chips/OwnerChip'
import { dealBlockerOpts } from 'util/Enums'
import { formatAbbreviatedMoney, formatPercent } from 'util/NumberUtils'
import { useOpportunityDetailsQuery } from 'graphql/bindings/AdminBindings'
import React, { Fragment } from 'react'
import RightAlign from 'components/shared/RightAlign'
import capitalize from 'lodash/capitalize'

interface OpportunityDetailsProps {
  opportunityId: string
}

export const OpportunityDetailsPanel: React.FC<OpportunityDetailsProps> = ({ opportunityId }) => {
  const { data } = useOpportunityDetailsQuery({ variables: { opportunityId } })
  const opportunity = data?.opportunity
  const tradeIntent = opportunity?.trade_intent
  const sharePrice = tradeIntent?.share_price
  const numShares = tradeIntent?.num_shares
  const certainty = opportunity?.certainty ?? ''
  const shareDecription = tradeIntent?.share_type_description ?? ''
  const lastRoundShares = opportunity?.company?.last_round_estimated_number_of_shares
  const impliedValuation = sharePrice && lastRoundShares ? lastRoundShares * sharePrice : undefined
  const fee = tradeIntent?.fee ?? ''
  const hasTimeConstraints = tradeIntent?.has_time_constraints ?? false
  const timeConstraints = tradeIntent?.time_constraint_notes ?? ''
  const isFlexible = tradeIntent?.has_flexibility ?? false
  const flexibilityNotes = tradeIntent?.flexibility_notes ?? ''
  const diligence = tradeIntent?.additional_diligence_notes ?? ''
  const [owner, ...otherOwners] = opportunity?.owners ?? []
  const dealBlockers = (opportunity?.deal_blockers ?? [])
    .map(blocker => dealBlockerOpts.find(elem => elem.value === blocker))
    .filter(blocker => !!blocker)
    .sort(
      (a, b) =>
        dealBlockerOpts.findIndex(elem => elem.value === a!.value) -
        dealBlockerOpts.findIndex(elem => elem.value === b!.value)
    ) as Array<{ label: string; value: string }>

  const [showModal, hideModal] = useModal(() => {
    return <OpportunityDetailsModal hideModal={hideModal} opportunityId={opportunityId} />
  }, [opportunityId])

  return (
    <Card>
      <CardHeader
        title="Opportunity Details"
        action={
          <IconButton onClick={showModal}>
            <PencilAltIcon />
          </IconButton>
        }
      />
      <CardContent>
        <LabelValueList
          items={[
            {
              label: 'Implied issuer valuation',
              value: formatAbbreviatedMoney(impliedValuation),
              isEmpty: !impliedValuation,
            },
            {
              label: 'Number of shares',
              value: (
                <Fragment>
                  {numShares && (
                    <Typography variant="body1" align="right">
                      {numShares}
                    </Typography>
                  )}
                  {shareDecription && (
                    <Typography variant="body1" align="right">
                      {shareDecription}
                    </Typography>
                  )}
                </Fragment>
              ),
              isEmpty: !numShares && !shareDecription,
            },
            {
              label: 'Certainty',
              value: capitalize(certainty),
              isEmpty: !certainty,
            },
            {
              label: 'Deal blockers',
              value: (
                <RightAlign>
                  {dealBlockers.map(blocker => (
                    <Chip label={blocker.label} variant="outlined" key={blocker.value} />
                  ))}
                </RightAlign>
              ),
              isEmpty: !dealBlockers.length,
            },
            {
              label: 'Fees',
              value: formatPercent(fee),
              isEmpty: fee.length === 0,
            },
            {
              label: 'Time constraints',
              value: (
                <Fragment>
                  <Typography variant="body1" align="right">
                    {hasTimeConstraints ? 'Has constraints' : 'No constraints'}
                  </Typography>
                  {hasTimeConstraints && (
                    <Typography variant="body1" align="right">
                      {timeConstraints}
                    </Typography>
                  )}
                </Fragment>
              ),
            },
            {
              label: 'Flexibility',
              value: (
                <Fragment>
                  <Typography variant="body1" align="right">
                    {isFlexible ? 'Flexible' : 'Not flexible'}
                  </Typography>
                  {isFlexible && (
                    <Typography variant="body1" align="right">
                      {flexibilityNotes}
                    </Typography>
                  )}
                </Fragment>
              ),
            },
            {
              label: 'Opportunity Owner',
              value: <OwnerChip owner={owner} />,
              isEmpty: !owner,
            },
            {
              label: 'Additional Forge Contact(s)',
              value: (
                <RightAlign>
                  {otherOwners.map(owner => (
                    <OwnerChip owner={owner} key={owner.id} />
                  ))}
                </RightAlign>
              ),
              isEmpty: !otherOwners.length,
            },
            {
              label: 'Additional diligence required',
              value: diligence,
              isEmpty: !diligence,
              fullWidth: !!diligence,
            },
          ]}
        />
      </CardContent>
    </Card>
  )
}
